import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Icon, Loader } from 'semantic-ui-react';
import { FlexAlign, FlexJustify } from '@src/types';
import { Flexbox } from '@src/views/containers';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { useSystemModals } from '@src/hooks';
import PracticeTabMenu from './PracticeTabMenu';
import ConsultantHourBudgetsDialog from './ConsultantHourBudgetsDialog';
import LogSowHoursDialog from './LogSowHoursDialog';
import { formatSowDateRange } from './sowOutlineUtils';
import { parseArrayPayload } from './offeringHoursData';
import type { SOWConsultantHours, SOWOutline } from './ClientOutlineTypes';

interface SOWTabProps {
    clientId: string;
    sow: SOWOutline;
    headerAside?: ReactNode;
    onOpenEditSowDialog?: ( sow: SOWOutline ) => void;
    onOpenAddOfferingDialog: ( sowId: string, sowName: string ) => void;
}

const formatBudgetedHours = ( hours?: number ): string | null => {
    if( hours == null || !Number.isFinite( hours ) ) return null;
    const rounded = Math.round( hours * 10 ) / 10;
    const formatted = Number.isInteger( rounded )
        ? `${ rounded }`
        : rounded.toLocaleString( undefined, { maximumFractionDigits: 1 } );
    return `${ formatted } hrs`;
};

const formatHoursCell = ( hours: number | null ): string => {
    if( hours == null || !Number.isFinite( hours ) ) return '—';
    return ( Math.round( hours * 100 ) / 100 ).toLocaleString( undefined, { maximumFractionDigits: 2 } );
};

const toNumberOrNull = ( value: unknown ): number | null => {
    if( value == null || value === '' ) return null;
    const parsed = Number( value );
    return Number.isFinite( parsed ) ? parsed : null;
};

const normalizeConsultantHours = ( rows: any[] ): SOWConsultantHours[] =>
    rows
        .filter( row => row?.ConsultantId )
        .map( row => ( {
            ConsultantSowHourLimitId: row.ConsultantSowHourLimitId ?? null,
            ConsultantId: `${ row.ConsultantId }`,
            ConsultantName: row.ConsultantName ?? null,
            BudgetedHours: toNumberOrNull( row.BudgetedHours ),
            AutoAdded: row.AutoAdded === true || row.AutoAdded === 1,
            LoggedHours: toNumberOrNull( row.LoggedHours ) ?? 0,
            RemainingHours: toNumberOrNull( row.RemainingHours ),
        } ) );

interface HoursProgressRowProps {
    label: string;
    budgeted: number | null;
    logged: number;
    isTotal?: boolean;
    autoAdded?: boolean;
    onLogHours?: () => void;
}

// One consultant (or the SOW total): name + figures on top, budget-usage bar below.
const HoursProgressRow: FC<HoursProgressRowProps> = ( { label, budgeted, logged, isTotal, autoAdded, onLogHours } ) => {
    const isUnbudgeted = budgeted == null;
    const remaining = isUnbudgeted ? null : budgeted - logged;
    const isOver = remaining != null && remaining < 0;
    const percent = isUnbudgeted
        ? 0
        : budgeted > 0 ? ( logged / budgeted ) * 100 : ( logged > 0 ? 100 : 0 );
    const isNearLimit = !isOver && percent >= 90;

    const stateClass = isUnbudgeted ? ' is-unbudgeted' : isOver ? ' is-over' : isNearLimit ? ' is-near' : '';

    return (
        <div className={ `sow-hours-row${ isTotal ? ' is-total' : '' }${ stateClass }` }>
            <div className={ 'sow-hours-row-head' }>
                <span className={ 'sow-hours-name' }>
                    <span className={ 'sow-hours-name-text' }>{ label }</span>
                    { autoAdded && (
                        <Icon
                            name={ 'sync alternate' }
                            className={ 'sow-hours-auto-added' }
                            title={ 'Auto-added from the payroll hours import. No hours were budgeted for this consultant.' }
                            aria-label={ 'Auto-added from payroll import' }
                        />
                    ) }
                    { onLogHours && (
                        <button
                            type={ 'button' }
                            className={ 'sow-hours-log-btn' }
                            title={ `Log hours for ${ label }` }
                            aria-label={ `Log hours for ${ label }` }
                            onClick={ onLogHours }
                        >
                            <Icon name={ 'plus' } />
                            Log
                        </button>
                    ) }
                </span>
                <span className={ 'sow-hours-figures' }>
                    { isUnbudgeted ? (
                        <>
                            <strong>{ formatHoursCell( logged ) }</strong> hrs logged
                            <span className={ 'sow-hours-status' }>Not budgeted</span>
                        </>
                    ) : (
                        <>
                            <strong>{ formatHoursCell( logged ) }</strong> / { formatHoursCell( budgeted ) } hrs
                            <span className={ 'sow-hours-status' }>
                                { isOver
                                    ? `${ formatHoursCell( Math.abs( remaining! ) ) } over`
                                    : `${ formatHoursCell( remaining ) } left` }
                            </span>
                        </>
                    ) }
                </span>
            </div>
            <div
                className={ 'sow-hours-bar' }
                role={ 'progressbar' }
                aria-label={ `${ label } hours used` }
                aria-valuemin={ 0 }
                aria-valuemax={ 100 }
                aria-valuenow={ Math.round( Math.min( percent, 100 ) ) }
            >
                <div className={ 'sow-hours-bar-fill' } style={ { width: `${ Math.min( percent, 100 ) }%` } } />
            </div>
        </div>
    );
};

const SOWTab: FC<SOWTabProps> = ( { clientId, sow, headerAside, onOpenEditSowDialog, onOpenAddOfferingDialog } ) => {

    const { executeStoredProcedure } = useCentralCalls();
    const modalActions = useSystemModals();

    const [ consultantHours, setConsultantHours ]       = useState<SOWConsultantHours[]>( [] ),
          [ hoursReloadKey, setHoursReloadKey ]         = useState( 0 ),
          [ hoursLoading, setHoursLoading ]             = useState( false ),
          [ hoursError, setHoursError ]                 = useState( '' );

    // Keyed on the sow object (not just SOWId) so a saved edit — which reloads the outline — refetches.
    useEffect( () => {
        if( !sow.SOWId ) return;
        let cancelled = false;

        const loadConsultantHours = async () => {
            setHoursLoading( true );
            setHoursError( '' );
            try {
                const response = await executeStoredProcedure( 'tng.sp_get_SOWConsultantHours', { sowId: sow.SOWId } );
                if( !cancelled ) setConsultantHours( normalizeConsultantHours( parseArrayPayload( response ) ) );
            } catch( err ){
                console.error( '[SOWTab] failed to load consultant hours:', err );
                if( !cancelled ){
                    setConsultantHours( [] );
                    setHoursError( 'Could not load consultant hours.' );
                }
            } finally {
                if( !cancelled ) setHoursLoading( false );
            }
        };

        loadConsultantHours();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ sow, hoursReloadKey ] );

    const handleOpenEdit = () => {
        onOpenEditSowDialog?.( sow );
    };

    const reloadConsultantHours = () => setHoursReloadKey( key => key + 1 );

    const handleOpenBudgets = () => {
        modalActions.show(
            <ConsultantHourBudgetsDialog
                sowId={ sow.SOWId }
                sowName={ sow.SOWName }
                sowBudgetedHours={ sow.BudgetedHours }
                onSaved={ reloadConsultantHours }
                onClose={ () => modalActions.toggle( false ) }
            />,
            { size: 'small', centered: false }
        );
    };

    const handleOpenLogHours = ( row: SOWConsultantHours ) => {
        modalActions.show(
            <LogSowHoursDialog
                sowId={ sow.SOWId }
                sowName={ sow.SOWName }
                consultantId={ row.ConsultantId }
                consultantName={ row.ConsultantName || 'Consultant' }
                budgetedHours={ row.BudgetedHours }
                loggedHours={ row.LoggedHours }
                onSaved={ reloadConsultantHours }
                onClose={ () => modalActions.toggle( false ) }
            />,
            { size: 'small', centered: false }
        );
    };

    const allocatedHours = Math.round( consultantHours.reduce( ( sum, row ) => sum + ( row.BudgetedHours ?? 0 ), 0 ) * 100 ) / 100;
    const totalLogged = consultantHours.reduce( ( sum, row ) => sum + row.LoggedHours, 0 );
    const hasSowBudget = sow.BudgetedHours != null && Number.isFinite( sow.BudgetedHours );
    // The SOW's own budget is the source of truth for the total; consultant budgets are a split of it.
    const totalBudgeted = hasSowBudget ? sow.BudgetedHours! : allocatedHours;
    const allocationMismatch = hasSowBudget && allocatedHours !== sow.BudgetedHours;

    const dateRangeText = formatSowDateRange( sow );

    const budgetedHoursLabel = formatBudgetedHours( sow.BudgetedHours );
    const leadConsultant = sow.SowLeadConsultant?.trim() || '';
    const hasExpansionFlag = typeof sow.ExpansionOpportunity === 'boolean';
    const showExpansion = sow.ExpansionOpportunity === true;

    return (
        <div>
            <Flexbox
                align={ FlexAlign.CENTER }
                justify={ FlexJustify.START }
                className={ 'sow-pane-meta' }
            >
                <span className={ 'sow-pane-meta-item' }>
                    <Icon name={ 'calendar outline' } className={ 'sow-pane-meta-icon' } />
                    <span className={ 'sow-pane-meta-text' }>{ dateRangeText }</span>
                    <Icon
                        name={ 'pencil' }
                        link
                        className={ 'sow-edit-dates-icon' }
                        title={ 'Edit SOW dates' }
                        aria-label={ 'Edit SOW dates' }
                        onClick={ handleOpenEdit }
                    />
                </span>

                { budgetedHoursLabel && (
                    <span
                        className={ 'sow-pane-meta-item sow-pane-meta-chip' }
                        title={ 'Budgeted hours' }
                    >
                        <Icon name={ 'clock outline' } className={ 'sow-pane-meta-icon' } />
                        <span className={ 'sow-pane-meta-text' }>{ budgetedHoursLabel }</span>
                    </span>
                ) }

                { leadConsultant && (
                    <span
                        className={ 'sow-pane-meta-item sow-pane-meta-chip' }
                        title={ 'SOW lead consultant' }
                    >
                        <Icon name={ 'user outline' } className={ 'sow-pane-meta-icon' } />
                        <span className={ 'sow-pane-meta-text' }>{ leadConsultant }</span>
                    </span>
                ) }

                { hasExpansionFlag && (
                    <span
                        className={ `sow-pane-meta-item sow-pane-meta-chip${ showExpansion ? ' sow-pane-meta-chip--expansion' : ' sow-pane-meta-chip--no-expansion' }` }
                        title={ showExpansion ? 'Expansion opportunity' : 'No expansion opportunity' }
                    >
                        <Icon name={ 'chart line' } className={ 'sow-pane-meta-icon' } />
                        <span className={ 'sow-pane-meta-text' }>
                            { showExpansion ? 'Expansion' : 'No expansion' }
                        </span>
                    </span>
                ) }

                { headerAside && (
                    <div className={ 'sow-pane-meta-aside' }>{ headerAside }</div>
                ) }
            </Flexbox>

            <div className={ 'sow-consultant-hours' }>
                <Flexbox
                    align={ FlexAlign.CENTER }
                    justify={ FlexJustify.START }
                    className={ 'sow-pane-offerings-header' }
                >
                    <span className={ 'sow-section-label' }>Consultant Hours</span>
                    <Icon
                        name={ 'pencil' }
                        link
                        className={ 'sow-edit-dates-icon' }
                        title={ 'Edit consultant hour budgets' }
                        aria-label={ 'Edit consultant hour budgets' }
                        onClick={ handleOpenBudgets }
                    />
                </Flexbox>

                { hoursLoading ? (
                    <Loader active inline size={ 'small' } />
                ) : hoursError ? (
                    <div className={ 'document-version-empty' }>{ hoursError }</div>
                ) : consultantHours.length === 0 ? (
                    <div className={ 'document-version-empty' }>
                        No consultant hours budgeted for this SOW yet.{ ' ' }
                        <button type={ 'button' } className={ 'sow-hours-empty-link' } onClick={ handleOpenBudgets }>
                            Add budgets
                        </button>
                    </div>
                ) : (
                    <div className={ 'sow-hours-card' }>
                        { consultantHours.map( row => (
                            <HoursProgressRow
                                key={ row.ConsultantId }
                                label={ row.ConsultantName || 'Unknown consultant' }
                                budgeted={ row.BudgetedHours }
                                logged={ row.LoggedHours }
                                autoAdded={ row.AutoAdded }
                                onLogHours={ () => handleOpenLogHours( row ) }
                            />
                        ) ) }
                        { ( hasSowBudget || consultantHours.length > 1 ) && (
                            <HoursProgressRow
                                isTotal
                                label={ hasSowBudget ? 'SOW Total' : 'Total' }
                                budgeted={ totalBudgeted }
                                logged={ totalLogged }
                            />
                        ) }
                        { allocationMismatch && (
                            <div className={ 'sow-hours-allocation-note' }>
                                <Icon name={ 'info circle' } />
                                { allocatedHours > sow.BudgetedHours!
                                    ? `Consultant budgets total ${ formatHoursCell( allocatedHours ) } hrs, ${ formatHoursCell( allocatedHours - sow.BudgetedHours! ) } more than the SOW budget.`
                                    : `${ formatHoursCell( sow.BudgetedHours! - allocatedHours ) } of ${ formatHoursCell( sow.BudgetedHours! ) } SOW hrs not assigned to a consultant.` }
                            </div>
                        ) }
                    </div>
                ) }
            </div>

            <Flexbox
                align={ FlexAlign.CENTER }
                justify={ FlexJustify.START }
                className={ 'sow-pane-offerings-header' }
            >
                <span className={ 'sow-section-label' }>Practices</span>
            </Flexbox>

            <PracticeTabMenu
                clientId={ clientId }
                sowId={ sow.SOWId }
                offerings={ sow.Offerings }
                onOpenAddOfferingDialog={ () => onOpenAddOfferingDialog( sow.SOWId, sow.SOWName ) }
                disableAddOffering={ !clientId || !sow.SOWId }
            />
        </div>
    );
};

export default SOWTab;
